/**
 * Sender name normalization for partner-prefilled profiles.
 *
 * Partners send names in varying shapes: multi-token values crammed into
 * first_name/last_name, or a single comma-delimited last_name carrying both
 * surnames. This module keeps those names intact while fitting them to the
 * Core API's field limits:
 *   - comma-splits last_name into lastName/secondLastName, keeping ALL parts
 *     (RAMPS-3600 introduced the split but silently dropped parts beyond the
 *     second); a partner-provided secondLastName takes precedence;
 *   - redistributes overflow beyond the API's 30-char per-field limit for
 *     lastName/secondLastName (initRegister validation rules) by spilling
 *     whole words into the empty sibling field, trimming at a word boundary
 *     as a last resort.
 *
 * RAMPS-4410: the previous stale-field dedup (clearing middleName/secondLastName
 * that repeated a sibling's trailing tokens, and collapsing adjacent duplicate
 * tokens within a field) was removed. It truncated legitimate names — e.g. a
 * customer whose lastName and secondLastName are both "James" had the second
 * surname silently dropped, so the transaction name no longer matched their ID
 * at pickup. The underlying name-quality problem is handled by a
 * pre-sendValidate verification step plus a backend change (RAMPS-4412).
 *
 * Pure functions — safe in the browser/iframe SDK context.
 */

/** API limit: initRegister validation allows max 30 chars for lastName/secondLastName. */
export const NAME_FIELD_MAX = 30;

/** SendValidate enforces a stricter 20-char limit on firstName specifically. */
export const FIRST_NAME_FIELD_MAX = 20;

/** SendValidate enforces a stricter 20-char limit on middleName specifically. */
export const MIDDLE_NAME_FIELD_MAX = 20;

/**
 * Characters the Core API rejects in name fields. The initRegister rules allow
 * only letters (incl. accented Latin U+00C0–U+017F), spaces, hyphens,
 * apostrophes, and slashes:
 *   regex = ^([a-zA-Z À-ſ\-'/])*$
 * This is the negated character class, for stripping disallowed input.
 */
// nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp -- literal, anchored char class, no backtracking risk
const NAME_FIELD_DISALLOWED = /[^a-zA-Z À-ſ\-'/]/g;

/**
 * Strip characters the API would reject from a name input (keeps letters,
 * accented Latin letters, spaces, hyphens, apostrophes, and slashes). Use on
 * name input onChange so users can't enter digits or special characters.
 */
export function sanitizeNameInput(value: string): string {
	return value.replace(NAME_FIELD_DISALLOWED, "");
}

export interface SenderNameFields {
	firstName: string;
	middleName: string;
	lastName: string;
	secondLastName: string;
}

/** Split into whitespace-normalized tokens; "" → []. */
function tokenize(value: string): string[] {
	return value.split(/\s+/).filter(Boolean);
}

/**
 * Move trailing whole words of an over-long `primary` into an empty
 * `secondary`; then trim both at word boundaries to their respective limits.
 * `primaryMax` and `secondaryMax` default to `NAME_FIELD_MAX` (30) but can
 * differ — e.g. firstName uses `FIRST_NAME_FIELD_MAX` (20) and middleName
 * uses `MIDDLE_NAME_FIELD_MAX` (20).
 */
function redistribute(
	primary: string[],
	secondary: string[],
	primaryMax = NAME_FIELD_MAX,
	secondaryMax = NAME_FIELD_MAX,
): { primary: string; secondary: string } {
	const primaryFits = (tokens: string[]) =>
		tokens.join(" ").length <= primaryMax;
	const secondaryFits = (tokens: string[]) =>
		tokens.join(" ").length <= secondaryMax;

	if (!primaryFits(primary) && secondary.length === 0) {
		// Spill trailing words until the primary fits (always keep one token).
		const spilled: string[] = [];
		while (!primaryFits(primary) && primary.length > 1) {
			spilled.unshift(primary.pop() as string);
		}
		if (secondaryFits(spilled)) {
			secondary = spilled;
		}
		// If the spill itself overflows, drop it back for word-boundary trim.
		else {
			primary = [...primary, ...spilled];
		}
	}

	return {
		primary: trimToWordBoundary(primary, primaryMax),
		secondary: trimToWordBoundary(secondary, secondaryMax),
	};
}

/** Drop trailing words until the value fits; hard-cut a single over-long word. */
function trimToWordBoundary(tokens: string[], max = NAME_FIELD_MAX): string {
	const kept = [...tokens];
	while (kept.join(" ").length > max && kept.length > 1) {
		kept.pop();
	}
	return kept.join(" ").slice(0, max);
}

/**
 * Normalize a partner-supplied name set: comma-split lastName, drop duplicated
 * tokens, and fit every field within the API's per-field character limits.
 */
export function normalizeSenderNames(profile: {
	firstName?: string | null;
	middleName?: string | null;
	lastName?: string | null;
	secondLastName?: string | null;
}): SenderNameFields {
	// Guard against undefined/null fields (e.g. v5 senderProfile may omit them).
	const rawFirst = profile.firstName ?? "";
	const rawMiddle = profile.middleName ?? "";
	const rawLast = profile.lastName ?? "";
	const rawSecond = profile.secondLastName ?? "";

	// Comma split (RAMPS-3600): "GONZÁLEZ, GARCIA DE MORALES" → lastName +
	// spillover. A partner-provided secondLastName keeps precedence, matching
	// the original calculator behavior.
	const commaParts = rawLast
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	const lastFromSplit = commaParts[0] ?? "";
	const spillFromSplit = commaParts.slice(1).join(" ");

	const first = tokenize(rawFirst);
	const middle = tokenize(rawMiddle);
	const last = tokenize(lastFromSplit);
	const second = tokenize(rawSecond.trim() ? rawSecond : spillFromSplit);

	// Fit per-field API limits, spilling into the empty sibling.
	// firstName is capped at FIRST_NAME_FIELD_MAX (20); middleName at MIDDLE_NAME_FIELD_MAX (20).
	const firstPair = redistribute(
		first,
		middle,
		FIRST_NAME_FIELD_MAX,
		MIDDLE_NAME_FIELD_MAX,
	);
	const lastPair = redistribute(last, second);

	return {
		firstName: firstPair.primary,
		middleName: firstPair.secondary,
		lastName: lastPair.primary,
		secondLastName: lastPair.secondary,
	};
}
